import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CartItem {
  productId: string;
  sku: string;
  name: string;
  price: number;           // price at time of add
  goldWeight?: number;     // gram
  qty: number;
  discount: number;        // absolute VND discount
  note: string;
  /** Set when item is part of an active flash sale (from evaluate endpoint). */
  flash_sale_id?: string;
  /** True when item is a BXGY gift (quantity/price locked). */
  is_gift?: boolean;
  /** Promotion that granted this gift item. */
  gift_promotion_id?: string;
}

interface POSCartState {
  items: CartItem[];
  globalDiscount: number;  // absolute VND
  customerId: string | null;
  note: string;
}

const initialState: POSCartState = {
  items: [],
  globalDiscount: 0,
  customerId: null,
  note: '',
};

const posCartSlice = createSlice({
  name: 'posCart',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<Omit<CartItem, 'qty' | 'discount' | 'note'>>) => {
      const existing = state.items.find((i) => i.productId === action.payload.productId);
      if (existing) {
        existing.qty += 1;
      } else {
        state.items.push({ ...action.payload, qty: 1, discount: 0, note: '' });
      }
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((i) => i.productId !== action.payload);
    },
    setQty: (state, action: PayloadAction<{ productId: string; qty: number }>) => {
      const item = state.items.find((i) => i.productId === action.payload.productId);
      if (item) item.qty = Math.max(1, action.payload.qty);
    },
    setItemDiscount: (state, action: PayloadAction<{ productId: string; discount: number }>) => {
      const item = state.items.find((i) => i.productId === action.payload.productId);
      if (item) item.discount = Math.max(0, action.payload.discount);
    },
    setItemNote: (state, action: PayloadAction<{ productId: string; note: string }>) => {
      const item = state.items.find((i) => i.productId === action.payload.productId);
      if (item) item.note = action.payload.note;
    },
    setGlobalDiscount: (state, action: PayloadAction<number>) => {
      state.globalDiscount = Math.max(0, action.payload);
    },
    setCustomer: (state, action: PayloadAction<string | null>) => {
      state.customerId = action.payload;
    },
    setOrderNote: (state, action: PayloadAction<string>) => {
      state.note = action.payload;
    },
    clearCart: () => initialState,
  },
});

export const {
  addItem,
  removeItem,
  setQty,
  setItemDiscount,
  setItemNote,
  setGlobalDiscount,
  setCustomer,
  setOrderNote,
  clearCart,
} = posCartSlice.actions;
export default posCartSlice.reducer;
